import React, { useEffect, useState, useCallback, useId, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "../constants/api";
import { showError, showSuccess } from "../utils/toast.js";
import { useTheme } from "../context/ThemeContext.jsx";
import { useConfirm } from "../components/ui/ConfirmationModal.jsx";
import { useAuth } from "../components/ui/AuthContext.jsx";
import { Check, ChevronDown } from "lucide-react";
import WartungsvertragInfoBlock from "../components/wartung/WartungsvertragInfoBlock.jsx";
import WartungBlockDateien from "../components/wartung/WartungBlockDateien.jsx";
import CollapsibleBlock from "../components/ui/CollapsibleBlock.jsx";
import ZuweisungBadge from "../components/ui/ZuweisungBadge.jsx";
import { Checkbox, Select, Input, Textarea } from "../components/forms/shared/FormAtoms.jsx";
import AppButton from "../components/ui/AppButton.jsx";
import { PdfExportActionSheet } from "../components/ui/PdfExportActions.jsx";
import useIsMobile from "../components/ui/useIsMobile.js";
import { sanitizeFileUrl } from "../utils/filePreview.js";
import { checklistErledigenGhostClasses, checklistItemCardShellClassName, checklistItemStripeRailClassName, detailPageHeaderNavClassName, detailPageHeaderNavActionsClassName, fileListDeleteActionClassName, fileListPreviewActionClassName, successButtonLoadingClassName, wartungRulesChoiceRowClass } from "../design/tokens.js";
import { smartBack } from "../constants/nav";
import { getPaketeForTyp } from "../constants/wartungConfig.js";

const wartungRulesRadioClass =
  "h-4 w-4 shrink-0 rounded-full border border-white/10 bg-surface-elevated accent-[var(--primary)] focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400/40";

function intervallToMonths(raw, fallback = 12) {
  const v = String(raw || "").trim().toLowerCase();
  const m = v.match(/^(\d+)\s*m$/);
  if (m) return Number(m[1]);
  const y = v.match(/^(\d+)\s*y$/);
  if (y) return Number(y[1]) * 12;
  const n = Number(v);
  return Number.isFinite(n) && n > 0 ? Math.round(n) : fallback;
}
export default function WartungsvertragDetail() {
  const {
    id
  } = useParams();
  const navigate = useNavigate();
    const confirm = useConfirm();
  const {
    user: me
  } = useAuth();
  const isMobile = useIsMobile();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [serviceRows, setServiceRows] = useState([]);
  const [activeServiceId, setActiveServiceId] = useState(null);
  const [creatingService, setCreatingService] = useState(false);
  const [protoResult, setProtoResult] = useState("IO");
  const [protoMassnahmen, setProtoMassnahmen] = useState("");
  const [creatingProto, setCreatingProto] = useState(false);
  const [exportingProtoId, setExportingProtoId] = useState(null);
  const [abschlussPdfSheet, setAbschlussPdfSheet] = useState(null);
  const [deletingProtoId, setDeletingProtoId] = useState(null);
  const [wartungChecklistByService, setWartungChecklistByService] = useState({});
  const [checklistSearch, setChecklistSearch] = useState("");
  const [editingRules, setEditingRules] = useState(false);
  const [savingRules, setSavingRules] = useState(false);
  const [users, setUsers] = useState([]);
  const [agbOptions, setAgbOptions] = useState([]);
  const [paketeByTyp, setPaketeByTyp] = useState({});
  const [rulesForm, setRulesForm] = useState({
    laufzeit_monate: 12,
    notify_days_before: 14,
    auto_renew: false,
    auto_service_enabled: true,
    paket_keys: [],
    assigned_user_ids: []
  });
  const formBaseId = useId();
  const fieldId = name => `wartungsvertrag-detail-${formBaseId}-${name}`;
  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [{
        data: v
      }, {
        data: s
      }] = await Promise.all([api.get(`/wartungsvertraege/${id}`), api.get(`/wartungsvertraege/${id}/serviceauftraege`)]);
      const vertrag = v?.data || null;
      const rows = Array.isArray(s?.data) ? s.data : [];
      setData(vertrag);
      setServiceRows(rows);
      setActiveServiceId(prev => {
        if (prev == null) return null;
        const ids = new Set(rows.map(r => Number(r.id)));
        return ids.has(Number(prev)) ? prev : null;
      });
    } catch {
      showError("Vertrag konnte nicht geladen werden.");
      navigate("/wartungsvertraege");
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);
  useEffect(() => {
    load();
  }, [load]);
  useEffect(() => {
    if (!data) return;
    const typKey = String(data.typ || "").toUpperCase();
    const configuredPakete = Array.isArray(paketeByTyp[typKey]) && paketeByTyp[typKey].length ? paketeByTyp[typKey] : getPaketeForTyp(data.typ);
    const paketKeysFromData = Array.isArray(data.pakete) ? Array.from(new Set(data.pakete.map(p => String(p?.paket_key || "").trim()).filter(Boolean))) : [];
    const defaultPaketKeys = configuredPakete.map(p => p.key);
    const effectivePaketKeys = paketKeysFromData.length ? [paketKeysFromData[0]] : defaultPaketKeys.length ? [defaultPaketKeys[0]] : [];
    const selectedCfg = configuredPakete.find(p => String(p.key) === String(effectivePaketKeys[0] || ""));
    const laufzeitFromPaket = selectedCfg ? intervallToMonths(selectedCfg.intervall, 12) : Number(data.laufzeit_monate ?? 12);
    setRulesForm({
      laufzeit_monate: laufzeitFromPaket,
      notify_days_before: Number(data.notify_days_before ?? 14),
      auto_renew: !!data.auto_renew,
      auto_service_enabled: data?.auto_service_enabled == null ? true : !!data.auto_service_enabled,
      paket_keys: effectivePaketKeys,
      assigned_user_ids: Array.isArray(data.assigned_user_ids) ? data.assigned_user_ids.map(Number).filter(n => n > 0) : []
    });
  }, [data, paketeByTyp]);
  useEffect(() => {
    const typ = String(data?.typ || "").toUpperCase();
    if (!typ) return;
    if (Array.isArray(paketeByTyp[typ]) && paketeByTyp[typ].length) return;
    (async () => {
      try {
        const {
          data: out
        } = await api.get("/wartung-konfig/pakete", {
          params: {
            typ
          }
        });
        const rows = Array.isArray(out?.pakete) ? out.pakete : [];
        if (!rows.length) return;
        setPaketeByTyp(prev => ({
          ...prev,
          [typ]: rows
        }));
      } catch {
        // fallback bleibt Code-Konfig
      }
    })();
  }, [data?.typ, paketeByTyp]);
  useEffect(() => {
    if (!editingRules || !data?.typ) return;
    (async () => {
      try {
        const u = await api.get("/service/users/options");
        setUsers(Array.isArray(u?.data) ? u.data : []);
      } catch {
        setUsers([]);
      }
      try {
        const a = await api.get("/agb", {
          params: {
            typ: data.typ
          }
        });
        setAgbOptions(Array.isArray(a?.data?.data) ? a.data.data : []);
      } catch {
        setAgbOptions([]);
      }
    })();
  }, [editingRules, data?.typ]);
  const fmtDate = v => {
    if (!v) return "–";
    const raw = String(v).trim();
    const d = /^\d{4}-\d{2}-\d{2}$/.test(raw) ? new Date(`${raw}T12:00:00`) : new Date(raw);
    if (Number.isNaN(d.getTime())) return "–";
    return d.toLocaleDateString("de-CH", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric"
    });
  };
  const fmtDateShort = v => {
    if (!v) return "";
    const d = new Date(v);
    if (Number.isNaN(d.getTime())) return "";
    return d.toLocaleString("de-CH", {
      day: "2-digit",
      month: "2-digit",
      hour: "2-digit",
      minute: "2-digit"
    });
  };
  const parseTemplateChecklist = useCallback(raw => {
    const arr = Array.isArray(raw) ? raw : [];
    return arr.map((it, idx) => {
      if (typeof it === "string") return {
        id: `w-${idx + 1}`,
        schritt: it.trim()
      };
      if (it && typeof it === "object") {
        return {
          id: String(it.id || `w-${idx + 1}`),
          schritt: String(it.schritt || it.label || it.title || "").trim()
        };
      }
      return null;
    }).filter(x => x && x.schritt).filter(x => String(x.schritt || "").trim().toLowerCase() !== "arbeiten erledigt").map(x => ({
      ...x,
      status: "OFFEN",
      erledigt_am: null,
      erledigt_von: null,
      erledigt_von_name: "",
      erledigt_von_avatar_url: null
    }));
  }, []);
  const parseProtocolChecklist = useCallback((raw, fallbackSteps = []) => {
    const arr = Array.isArray(raw) ? raw : [];
    if (!arr.length) {
      return fallbackSteps.map(s => ({
        id: s.id,
        schritt: s.schritt,
        status: "OFFEN",
        erledigt_am: null,
        erledigt_von: null,
        erledigt_von_name: "",
        erledigt_von_avatar_url: null
      }));
    }
    return arr.map((it, idx) => ({
      id: String(it?.id || `w-${idx + 1}`),
      schritt: String(it?.schritt || it?.label || it?.title || `Schritt ${idx + 1}`),
      status: String(it?.status || "OFFEN").toUpperCase() === "ERLEDIGT" ? "ERLEDIGT" : "OFFEN",
      erledigt_am: it?.erledigt_am || null,
      erledigt_von: it?.erledigt_von || null,
      erledigt_von_name: it?.erledigt_von_name || "",
      erledigt_von_avatar_url: it?.erledigt_von_avatar_url || null
    })).filter(x => String(x.schritt || "").trim().toLowerCase() !== "arbeiten erledigt");
  }, []);
  const latestChecklistTemplate = useMemo(() => {
    const list = Array.isArray(data?.checklisten) ? data.checklisten : [];
    const latest = list[0];
    return parseTemplateChecklist(latest?.checklist_json);
  }, [data?.checklisten, parseTemplateChecklist]);
  const latestProtoForActiveService = useMemo(() => {
    if (!activeServiceId) return null;
    const rows = Array.isArray(data?.abschlussprotokolle) ? data.abschlussprotokolle.filter(p => Number(p.serviceauftrag_id) === Number(activeServiceId)) : [];
    const sorted = rows.slice().sort((a, b) => {
      const ta = Date.parse(String(a?.created_at ?? "")) || 0;
      const tb = Date.parse(String(b?.created_at ?? "")) || 0;
      return tb - ta;
    });
    return sorted[0] || null;
  }, [activeServiceId, data?.abschlussprotokolle]);
  useEffect(() => {
    if (!activeServiceId) return;
    const sid = Number(activeServiceId);
    const already = Array.isArray(wartungChecklistByService[sid]) && wartungChecklistByService[sid].length > 0;
    if (already) return;
    const fromProto = parseProtocolChecklist(latestProtoForActiveService?.checklist_result_json, latestChecklistTemplate);
    setWartungChecklistByService(prev => ({
      ...prev,
      [sid]: fromProto
    }));
  }, [activeServiceId, latestChecklistTemplate, latestProtoForActiveService, parseProtocolChecklist, wartungChecklistByService]);
  const currentWartungChecklist = useMemo(() => {
    const sid = Number(activeServiceId || 0);
    if (!sid) return [];
    return Array.isArray(wartungChecklistByService[sid]) ? wartungChecklistByService[sid] : [];
  }, [activeServiceId, wartungChecklistByService]);
  const checklistProgress = useMemo(() => {
    const total = currentWartungChecklist.length;
    const done = currentWartungChecklist.filter(it => it.status === "ERLEDIGT").length;
    const percent = total ? Math.round(done / total * 100) : 0;
    return {
      done,
      total,
      percent
    };
  }, [currentWartungChecklist]);
  const checklistQuery = String(checklistSearch || "").trim().toLowerCase();
  const visibleWartungChecklist = useMemo(() => {
    if (!checklistQuery) return currentWartungChecklist;
    return currentWartungChecklist.filter(it => [it?.schritt, it?.erledigt_von_name].filter(Boolean).some(val => String(val).toLowerCase().includes(checklistQuery)));
  }, [currentWartungChecklist, checklistQuery]);
  const checklistScrollFrom8 = visibleWartungChecklist.length >= 8;
  const checklistMaxListPx = 8 * 94;
  function toggleWartungStep(stepId) {
    const sid = Number(activeServiceId || 0);
    if (!sid) return;
    setWartungChecklistByService(prev => {
      const list = Array.isArray(prev[sid]) ? prev[sid] : [];
      const next = list.map(it => String(it.id) === String(stepId) ? {
        ...it,
        status: it.status === "ERLEDIGT" ? "OFFEN" : "ERLEDIGT",
        erledigt_am: it.status === "ERLEDIGT" ? null : new Date().toISOString(),
        erledigt_von: it.status === "ERLEDIGT" ? null : me?.id || null,
        erledigt_von_name: it.status === "ERLEDIGT" ? "" : me?.name || [me?.vorname, me?.nachname].filter(Boolean).join(" ").trim() || "",
        erledigt_von_avatar_url: it.status === "ERLEDIGT" ? null : me?.avatar_path || me?.avatar_url || null
      } : it);
      return {
        ...prev,
        [sid]: next
      };
    });
  }
  async function createServiceauftrag() {
    try {
      setCreatingService(true);
      const out = await api.post(`/wartungsvertraege/${id}/create-serviceauftrag`, {});
      if (out?.data?.code === "ALREADY_EXISTS") {
        showSuccess("Es existiert bereits ein Serviceauftrag für diese Periode.");
      } else {
        showSuccess("Serviceauftrag wurde erstellt.");
      }
      await load();
      if (out?.data?.serviceauftragId) setActiveServiceId(out.data.serviceauftragId);
    } catch {
      showError("Serviceauftrag-Erzeugung fehlgeschlagen.");
    } finally {
      setCreatingService(false);
    }
  }
  async function archiveVertrag() {
    const {
      ok
    } = await confirm({
      title: "Wartungsvertrag archivieren",
      message: "Möchtest du diesen Wartungsvertrag wirklich archivieren?",
      variant: "danger",
      confirmLabel: "Archivieren"
    });
    if (!ok) return;
    try {
      await api.put(`/wartungsvertraege/${id}`, {
        status: "ARCHIVIERT"
      });
      showSuccess("Wartungsvertrag archiviert.");
      await load();
      navigate("/wartungsvertraege");
    } catch {
      showError("Archivieren fehlgeschlagen.");
    }
  }
  async function createAbschlussprotokollForService() {
    if (!activeServiceId) {
      showError("Bitte zuerst einen Serviceauftrag auswählen.");
      return;
    }
    try {
      setCreatingProto(true);
      const checklistItems = Array.isArray(currentWartungChecklist) ? currentWartungChecklist : [];
      const checklist_result_json = checklistItems.map(it => ({
        id: it.id,
        schritt: it.schritt,
        status: String(it.status || "OFFEN").toUpperCase(),
        erledigt_am: it.erledigt_am || null
      }));
      await api.post("/abschlussprotokolle", {
        wartungsvertrag_id: Number(id),
        serviceauftrag_id: Number(activeServiceId),
        status_result: protoResult,
        massnahmen_text: protoMassnahmen || null,
        checklist_result_json
      });
      showSuccess("Abschlussprotokoll wurde gespeichert.");
      await load();
    } catch {
      showError("Abschlussprotokoll konnte nicht erstellt werden.");
    } finally {
      setCreatingProto(false);
    }
  }
  async function exportAbschlussprotokoll(protoId) {
    try {
      setExportingProtoId(protoId);
      const {
        data: out
      } = await api.post(`/abschlussprotokolle/${protoId}/export-pdf`, {});
      showSuccess("Kundenprotokoll als PDF erstellt.");
      const rawUrl = out?.secure_preview_url || out?.secure_download_url || out?.secure_url || "";
      const safe = sanitizeFileUrl(rawUrl);
      if (!safe) {
        showError("PDF-Link fehlt oder ist ungültig.");
      } else {
        setAbschlussPdfSheet({
          pdfUrl: rawUrl,
          title: "Abschlussprotokoll PDF",
          fileName: `abschlussprotokoll-${protoId}.pdf`,
          previewModule: "maintenance_vertrag_document_files",
          fileId: out?.id ?? null,
        });
      }
      await load();
    } catch {
      showError("Export Abschlussprotokoll fehlgeschlagen.");
    } finally {
      setExportingProtoId(null);
    }
  }
  async function deleteAbschlussprotokoll(protoId) {
    const {
      ok
    } = await confirm({
      title: "Abschlussprotokoll löschen",
      message: "Möchtest du dieses Abschlussprotokoll wirklich löschen?",
      variant: "danger",
      confirmLabel: "Löschen"
    });
    if (!ok) return;
    try {
      setDeletingProtoId(protoId);
      await api.delete(`/abschlussprotokolle/${protoId}`);
      showSuccess("Abschlussprotokoll gelöscht.");
      await load();
    } catch {
      showError("Abschlussprotokoll konnte nicht gelöscht werden.");
    } finally {
      setDeletingProtoId(null);
    }
  }
  async function saveRules() {
    try {
      setSavingRules(true);
      const paketMap = new Map(packageDefs.map(p => [String(p.key), p]));
      const selectedKey = Array.isArray(rulesForm.paket_keys) ? String(rulesForm.paket_keys[0] || "") : "";
      const selectedCfg = paketMap.get(selectedKey);
      if (!selectedCfg) {
        showError("Bitte genau ein Paket auswählen.");
        return;
      }
      const paketePayload = selectedCfg ? [{
        paket_key: selectedCfg.key,
        modul_key: null,
        preis_chf: Number(selectedCfg.preis_chf || 0),
        intervall: selectedCfg.intervall || "12m",
        inkludierte_stunden: selectedCfg.inkludierte_stunden == null ? null : Number(selectedCfg.inkludierte_stunden),
        inkludiertes_material_chf: selectedCfg.inkludiertes_material_chf == null ? null : Number(selectedCfg.inkludiertes_material_chf)
      }] : [];
      const laufzeitMonate = selectedCfg ? intervallToMonths(selectedCfg.intervall, 12) : Number(rulesForm.laufzeit_monate || 12);
      const activeAgb = agbOptions.find(a => a?.active) || agbOptions[0] || null;
      await api.put(`/wartungsvertraege/${id}`, {
        laufzeit_monate: laufzeitMonate,
        notify_days_before: Number(rulesForm.notify_days_before || 14),
        auto_renew: !!rulesForm.auto_renew,
        auto_service_enabled: !!rulesForm.auto_service_enabled,
        agb_version_id: activeAgb?.id ? Number(activeAgb.id) : null,
        pakete: paketePayload,
        assigned_user_ids: Array.isArray(rulesForm.assigned_user_ids) ? rulesForm.assigned_user_ids.map(Number).filter(n => n > 0) : []
      });
      showSuccess("Reminder / Bestimmungen gespeichert.");
      setEditingRules(false);
      await load();
    } catch {
      showError("Speichern fehlgeschlagen.");
    } finally {
      setSavingRules(false);
    }
  }
  const cardCls = "rounded-2xl border border-white/10 bg-[#0b1324] text-blue-50";
  const grad = "min-h-screen w-full bg-gradient-to-br from-[#05070c] via-[#0a1020] to-[#0a1a32] text-white";
  const inputCls = "w-full border rounded px-3 py-2 text-sm mt-1 bg-[#0b1324] text-blue-100 border-white/10 focus:outline-none focus:ring-2 focus:ring-blue-400/40";
  const title = (data?.titel || "").trim() || `Wartungsvertrag #${id}`;
  const activeService = serviceRows.find(s => Number(s.id) === Number(activeServiceId)) || null;
  const protokolleByService = useMemo(() => {
    const all = Array.isArray(data?.abschlussprotokolle) ? data.abschlussprotokolle : [];
    const list = activeServiceId ? all.filter(p => Number(p.serviceauftrag_id) === Number(activeServiceId)) : [];
    return list.slice().sort((a, b) => {
      const ta = Date.parse(String(a?.created_at ?? "")) || 0;
      const tb = Date.parse(String(b?.created_at ?? "")) || 0;
      return tb - ta;
    });
  }, [data?.abschlussprotokolle, activeServiceId]);
  const selectedPackages = Array.isArray(data?.pakete) ? Array.from(new Set(data.pakete.map(p => String(p?.paket_key || "").trim()).filter(Boolean))) : [];
  const packageDefs = useMemo(() => {
    const typ = String(data?.typ || "").toUpperCase();
    if (typ && Array.isArray(paketeByTyp[typ]) && paketeByTyp[typ].length) return paketeByTyp[typ];
    return getPaketeForTyp(data?.typ);
  }, [data?.typ, paketeByTyp]);
  const packageLabelMap = new Map(packageDefs.map(p => [String(p.key), p.label]));
  const selectedPackageLabels = selectedPackages.slice(0, 1).map(k => packageLabelMap.get(k) || k);
  const notifiedUsers = Array.isArray(data?.assigned_users) ? data.assigned_users : [];
  const serviceTitle = s => {
    const raw = String(s?.titel || "").trim();
    if (raw) return raw;
    if (s?.periode_label) return `Wartung ${s.periode_label}`;
    return "Wartung";
  };
  const protokollResultLabel = value => {
    const key = String(value || "").toUpperCase();
    if (key === "IO") return "Alles in Ordnung";
    if (key === "MASSNAHMEN_ERFORDERLICH") return "Massnahmen erforderlich";
    if (key === "EMPFOHLEN") return "Massnahmen empfohlen";
    return key || "-";
  };
  const protokollResultTone = value => {
    const key = String(value || "").toUpperCase();
    if (key === "IO") {
      return "border-green-400/30 bg-green-500/10 text-green-200";
    }
    if (key === "MASSNAHMEN_ERFORDERLICH") {
      return "border-orange-400/35 bg-orange-500/10 text-orange-200";
    }
    return "border-sky-400/30 bg-sky-500/10 text-sky-200";
  };
  const checklistAmpelColor = p => {
    const val = Math.max(0, Math.min(100, Math.round(Number(p) || 0)));
    if (val < 25) return "#ef4444";
    if (val < 50) return "#f97316";
    if (val < 75) return "#eab308";
    return "#22c55e";
  };
  return <main className={grad}>
      <div className="mx-auto max-w-6xl px-4 py-6 grid gap-3">
        <div className={`rounded-2xl px-4 py-3 border flex flex-col sm:flex-row sm:items-center sm:justify-between mb-1 app-frame`}>
          <div className="min-w-0">
            <h1 className={`text-2xl font-bold tracking-tight flex items-center gap-2 ${"text-blue-100/90"}`}>
              <span aria-hidden>🔧</span>
              <span className="truncate">{title}</span>
            </h1>
          </div>
          <div className={`${detailPageHeaderNavActionsClassName} mt-3 sm:mt-0`}>
            <AppButton type="button" variant="secondary" size="lg" className={detailPageHeaderNavClassName} onClick={() => smartBack(navigate, ["/wartungsvertraege", "/"])}>
              Zurück
            </AppButton>
            <AppButton type="button" variant="secondary" size="lg" className={detailPageHeaderNavClassName} onClick={() => navigate("/wartungsvertraege")}>
              Wartungsliste
            </AppButton>
            <AppButton type="button" variant="secondary" size="lg" className={detailPageHeaderNavClassName} onClick={() => navigate("/meine-aufgaben")}>
              Meine Aufgaben
            </AppButton>
            <AppButton type="button" variant="secondary" size="lg" className={detailPageHeaderNavClassName} onClick={() => navigate("/leitstand/alle")}>
              Übersicht
            </AppButton>
            <AppButton type="button" variant="secondary" size="lg" className={detailPageHeaderNavClassName} onClick={() => navigate("/profil")}>
              Mein Profil
            </AppButton>
            <AppButton type="button" variant="secondary" size="lg" className={detailPageHeaderNavClassName} onClick={() => navigate("/")}>
              Dashboard
            </AppButton>
          </div>
        </div>

        <WartungsvertragInfoBlock vertrag={data} onSaved={load} onBearbeiten={() => navigate(`/wartungsvertraege/${id}/bearbeiten`)} onCreateServiceauftrag={createServiceauftrag} onArchive={archiveVertrag} />

      <CollapsibleBlock title="🔔 Reminder / Bestimmungen" headerInfo={`${Number(data?.notify_days_before || 14)} Tg. Vorlauf`} defaultOpen={false} contentClassName="px-3 sm:px-4 pb-3 sm:pb-4">
        <div className="pt-2">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
          <div className={`rounded-xl border p-3 ${"border-white/10 bg-white/[0.02]"}`}>
            <div className="text-xs uppercase tracking-wide opacity-70 mb-2">Automatik / Laufzeit</div>
            {!editingRules ? <div className="space-y-1.5">
                <div>Laufzeit: <span className="font-medium">{data?.laufzeit_monate ? `${data.laufzeit_monate} Monate` : "–"}</span></div>
                <div>Auto-Renew: <span className="font-medium">{data?.auto_renew ? "Ja" : "Nein"}</span></div>
                <div>Reminder: <span className="font-medium">{Number(data?.notify_days_before || 14)} Tage vorher</span></div>
                <div>Automatische Serviceerstellung: <span className="font-medium">{data?.auto_service_enabled == null ? "Aktiv" : data?.auto_service_enabled ? "Aktiv" : "Pausiert"}</span></div>
                {String(data?.status || "").toUpperCase() === "PAUSIERT" ? <div className={`text-xs ${"text-amber-300/90"}`}>
                    Hinweis: Bei "Pausiert" erfolgen keine automatischen Benachrichtigungen.
                  </div> : null}
                {data?.auto_service_enabled === false ? <div className={`text-xs ${"text-amber-300/90"}`}>
                    Hinweis: Automatische Serviceerstellung ist separat pausiert.
                  </div> : null}
              </div> : <div className="space-y-3">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <label className="text-sm">Laufzeit (Monate)
                    <Input type="number" min={1} className={inputCls} value={rulesForm.laufzeit_monate} readOnly />
                    <div className={`text-xs mt-1 ${"text-blue-200/70"}`}>Wird automatisch vom gewählten Paket übernommen.</div>
                  </label>
                  <label className="text-sm">Reminder (Tage vorher)
                    <Input type="number" min={0} className={inputCls} value={rulesForm.notify_days_before} onChange={e => setRulesForm(s => ({
                      ...s,
                      notify_days_before: e.target.value
                    }))} />
                  </label>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-[minmax(0,1fr)_auto] gap-3">
                  <label className={[wartungRulesChoiceRowClass(!!rulesForm.auto_service_enabled), "!py-1.5 min-w-0"].join(" ")}>
                    <Checkbox checked={!!rulesForm.auto_service_enabled} onChange={e => setRulesForm(s => ({
                      ...s,
                      auto_service_enabled: e.target.checked
                    }))} />
                    <span className="min-w-0 whitespace-nowrap">Automatische Serviceerstellung</span>
                  </label>
                  <label className={[wartungRulesChoiceRowClass(!!rulesForm.auto_renew), "!py-1.5 w-full md:w-max md:max-w-none shrink-0"].join(" ")}>
                    <Checkbox checked={!!rulesForm.auto_renew} onChange={e => setRulesForm(s => ({
                      ...s,
                      auto_renew: e.target.checked
                    }))} />
                    <span className="whitespace-nowrap">Auto-Renew</span>
                  </label>
                </div>
              </div>}
          </div>

          <div className={`rounded-xl border p-3 ${"border-white/10 bg-white/[0.02]"}`}>
            <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
              <div className="text-xs uppercase tracking-wide opacity-70 min-w-0">Vertrag / Paket / AGB</div>
              <div className="flex flex-wrap items-center justify-end gap-2 shrink-0">
                {!editingRules ? <AppButton type="button" variant="primary" size="sm" onClick={() => setEditingRules(true)}>
                    Bearbeiten
                  </AppButton> : <>
                    <AppButton type="button" variant="secondary" size="sm" disabled={savingRules} className="!border-white/10 hover:!bg-white/[0.06]" onClick={() => {
                    setEditingRules(false);
                    setRulesForm({
                      laufzeit_monate: Number(data?.laufzeit_monate ?? 12),
                      notify_days_before: Number(data?.notify_days_before ?? 14),
                      auto_renew: !!data?.auto_renew,
                      auto_service_enabled: data?.auto_service_enabled == null ? true : !!data.auto_service_enabled,
                      paket_keys: Array.isArray(data?.pakete) ? Array.from(new Set(data.pakete.map(p => String(p?.paket_key || "").trim()).filter(Boolean))).slice(0, 1) : packageDefs[0]?.key ? [packageDefs[0].key] : [],
                      assigned_user_ids: Array.isArray(data?.assigned_user_ids) ? data.assigned_user_ids.map(Number).filter(n => n > 0) : []
                    });
                  }}>
                      Abbrechen
                    </AppButton>
                    <AppButton type="button" variant="success" size="sm" disabled={savingRules} className={savingRules ? successButtonLoadingClassName : ""} onClick={saveRules}>
                      {savingRules ? "Speichere..." : "Speichern"}
                    </AppButton>
                  </>}
              </div>
            </div>
            <div className="space-y-1.5">
              {!editingRules ? <div>Paket: <span className="font-medium">{selectedPackageLabels.length ? selectedPackageLabels[0] : "–"}</span></div> : <div>
                  <div className="text-sm mb-1">Paket (genau eines)</div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                    {packageDefs.map(pkg => {
                      const key = String(pkg.key);
                      const checked = Array.isArray(rulesForm.paket_keys) && String(rulesForm.paket_keys[0] || "") === key;
                      return <label key={key} className={wartungRulesChoiceRowClass(checked)}>
                          <input type="radio" name={fieldId("paket-single")} checked={checked} onChange={() => setRulesForm(s => ({
                          ...s,
                          paket_keys: [key],
                          laufzeit_monate: intervallToMonths(pkg.intervall, 12)
                        }))} className={wartungRulesRadioClass} />
                          <span className="font-medium">{pkg.label}</span>
                        </label>;
                    })}
                  </div>
                </div>}
              {!editingRules ? <div>AGB: <span className="font-medium">{data?.agb_version_id ? "Aktuelle AGB" : "–"}</span></div> : <div>AGB: <span className="font-medium">Aktuelle AGB (pro Typ)</span></div>}
              <div>Typ: <span className="font-medium">{data?.typ || "–"}</span></div>
            </div>
          </div>

          <div className={`rounded-xl border p-3 md:col-span-2 ${"border-white/10 bg-white/[0.02]"}`}>
            <div className="text-xs uppercase tracking-wide opacity-70 mb-2">Benachrichtigt werden</div>
            {!editingRules ? notifiedUsers.length ? <div className="text-sm opacity-90">
                  {notifiedUsers.map(u => String(u?.name || `User #${u?.id || "-"}`)).join(", ")}
                </div> : <div className="text-sm text-blue-200/85">Keine expliziten Nutzer hinterlegt (Fallback auf Verantwortung/Ersteller).</div> : <div className={`grid grid-cols-1 md:grid-cols-2 gap-2.5 max-h-[220px] overflow-y-auto p-2.5 rounded border ${"border-white/10 bg-white/[0.02]"}`}>
                {users.length === 0 ? <div className="md:col-span-2 text-sm text-blue-200/80 px-1 py-1.5">
                    Keine Benutzer geladen. Bitte kurz neu öffnen oder speichern/neu laden.
                  </div> : users.map(u => {
                  const uid = Number(u.id);
                  const checked = rulesForm.assigned_user_ids.includes(uid);
                  return <label key={u.id} className={wartungRulesChoiceRowClass(checked)}>
                      <Checkbox checked={checked} onChange={() => setRulesForm(s => ({
                      ...s,
                      assigned_user_ids: checked ? s.assigned_user_ids.filter(x => x !== uid) : [...s.assigned_user_ids, uid]
                    }))} />
                      <span>{u.name}</span>
                    </label>;
                })}
              </div>}
          </div>
        </div>
        </div>
      </CollapsibleBlock>

      <WartungBlockDateien vertragId={Number(id)} />

      <CollapsibleBlock title="🔗 Verknüpfte Serviceaufträge" headerInfo={`${serviceRows.length} verknüpft`} defaultOpen={false} contentClassName="px-3 sm:px-4 pb-3 sm:pb-4">
        <div className="pt-2">
          {!serviceRows.length ? <div className={`text-sm text-blue-200/85 rounded-xl border px-3 py-2 ${"border-white/10 bg-white/[0.02]"}`}>Noch keine verknüpften Serviceaufträge.</div> : <div className="space-y-2">
              {serviceRows.map(s => {
              const selected = Number(activeServiceId) === Number(s.id);
              const serviceRowPreview = [s.periode_label ? `Periode ${s.periode_label}` : null, s.status ? String(s.status) : null].filter(Boolean).join(" · ") || "Details anzeigen";
              const servicePanelId = `wartung-verkn-svc-${s.id}`;
              return <div key={s.id} className={`rounded-xl border overflow-hidden transition ${selected ? "border-green-400/45 bg-green-500/[0.07] shadow-[0_0_0_1px_rgba(34,197,94,0.2)]" : "border-white/10 bg-white/[0.02]"}`}>
                  <AppButton
                    type="button"
                    variant="ghost"
                    id={`${servicePanelId}-trigger`}
                    aria-expanded={selected}
                    aria-controls={servicePanelId}
                    className={`w-full !justify-start text-left !h-auto !min-h-0 !py-2 !px-3 !gap-2 !font-normal rounded-none !border-0 !shadow-none !bg-transparent transition focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-blue-400/35 ${selected ? "" : "hover:!bg-white/[0.04]"}`}
                    onClick={() => setActiveServiceId(prev => Number(prev) === Number(s.id) ? null : Number(s.id))}
                  >
                    <div className="flex items-start justify-between gap-2 w-full">
                      <div className="flex items-start gap-2 min-w-0 flex-1">
                        <ChevronDown className={`w-4 h-4 shrink-0 mt-0.5 text-muted transition-transform duration-200 ${selected ? "rotate-180" : "-rotate-90"}`} aria-hidden />
                        <div className="min-w-0">
                          <div className="text-sm font-semibold truncate">{serviceTitle(s)}</div>
                          {selected ? <div className="text-xs text-blue-200/80 mt-0.5">
                              {s.periode_label ? `Fälligkeitsperiode ${s.periode_label}` : "Fälligkeitsperiode –"}
                              {" · "}
                              Erstellt {fmtDate(s.created_at)}
                            </div> : <div className="text-xs text-muted mt-0.5 truncate">{serviceRowPreview}</div>}
                          {selected ? <>
                              <div className="text-xs text-blue-200/80 mt-0.5">
                                Fällig am: {fmtDate(s.wartungsfaelligkeit)}
                              </div>
                              <div className="text-xs text-blue-200/80 mt-0.5">Status: {s.status || "-"}</div>
                            </> : null}
                        </div>
                      </div>
                      {selected ? <span className={`text-[10px] px-2 py-0.5 rounded-full border shrink-0 mt-0.5 ${"border-green-400/40 bg-green-500/20 text-green-100"}`}>aktiv</span> : null}
                    </div>
                  </AppButton>
                  {selected ? <>
                      <div className="px-3 pt-1 pb-2 flex justify-end">
                        <AppButton type="button" variant="secondary" size="sm" className="!bg-white/10 hover:!bg-white/15 !text-blue-100 !border-white/15 text-xs" onClick={() => navigate(`/service/${s.id}`)}>
                          Service-Detail öffnen
                        </AppButton>
                      </div>
                      <div id={servicePanelId} role="region" aria-labelledby={`${servicePanelId}-trigger`} className="border-t border-white/10 bg-[#090f1a]/80 px-3 sm:px-4 py-4 space-y-4">
            <div className="rounded-2xl border border-default bg-surface overflow-hidden">
              <div className="px-3 sm:px-4 pt-3 pb-2 border-b border-default flex flex-wrap items-start justify-between gap-2">
                <div className="font-semibold text-primary flex items-center gap-2 min-w-0">
                  <span className="text-emerald-400" aria-hidden>✅</span>
                  <span className="truncate">Wartungs-Checkliste</span>
                </div>
                <div className="text-xs text-muted shrink-0">
                  {checklistProgress.done}/{checklistProgress.total} · {checklistProgress.percent}%
                  {checklistProgress.total > 0 && checklistProgress.done === checklistProgress.total ? " · abgeschlossen" : ""}
                </div>
              </div>
              <div className="px-3 sm:px-4 pb-3 sm:pb-4">
                <div className="sticky top-0 z-10 -mx-3 sm:-mx-4 px-3 sm:px-4 py-2 bg-surface-elevated border-b border-default">
                  <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                    <div className="relative w-full min-w-0 sm:flex-1 sm:min-w-[220px]">
                      <Input type="text" inputMode="search" autoComplete="off" enterKeyHint="search" placeholder="Suche…" title="Schritt, Beschreibung oder Person …" value={checklistSearch} onChange={e => setChecklistSearch(e.target.value)} className={`px-3 py-2 min-h-[40px] w-full text-sm [appearance:textfield] [&::-webkit-search-cancel-button]:hidden ${checklistSearch ? "pr-10" : "pr-3"}`} style={isMobile ? {
                    fontSize: "16px"
                  } : undefined} aria-label="Wartungs-Checkliste durchsuchen" role="searchbox" />
                      {checklistSearch ? <AppButton type="button" variant="ghost" onClick={() => setChecklistSearch("")} className="absolute right-1 top-1/2 -translate-y-1/2 !min-h-[36px] !min-w-[36px] !p-0 !border-0 !bg-transparent !shadow-none text-blue-200/70 hover:!bg-transparent hover:text-white" aria-label="Suche löschen" title="Suche löschen">
                          ✕
                        </AppButton> : null}
                    </div>
                    <div className="flex items-center gap-3 sm:ml-auto">
                      <div className="w-28 h-2 rounded-full border border-default bg-muted overflow-hidden" aria-hidden>
                        <div className="h-full transition-all" style={{
                        width: `${Math.max(0, Math.min(100, checklistProgress.percent))}%`,
                        background: checklistAmpelColor(checklistProgress.percent)
                      }} />
                      </div>
                      <div className="text-xs text-muted" aria-label="Fortschritt">
                        {checklistProgress.done} / {checklistProgress.total}
                      </div>
                    </div>
                  </div>
                </div>

                {!currentWartungChecklist.length ? <div className="text-sm text-muted pt-3">Keine Wartungs-Checkliste vorhanden.</div> : visibleWartungChecklist.length === 0 ? <div className="p-3 text-sm italic text-muted">
                    {checklistSearch ? "Keine Treffer für die Suche." : "Noch keine Punkte vorhanden."}
                  </div> : <div className={`${checklistScrollFrom8 ? "overflow-y-auto" : "overflow-visible"} mt-2`} style={{
                maxHeight: checklistScrollFrom8 ? `${checklistMaxListPx}px` : "none"
              }} aria-live="polite">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 md:gap-3">
                      {visibleWartungChecklist.map((it, idx) => {
                        const erledigt = it.status === "ERLEDIGT";
                        const stripeItem = {
                          erledigt,
                          nicht_noetig: false,
                          in_arbeit: false
                        };
                        const titleText = `${idx + 1}. ${it.schritt || "—"}`;
                        return <div key={`${it.id}-${idx}`} className={`flex overflow-hidden rounded-2xl border transition-colors ${checklistItemCardShellClassName(true)}`}>
                            <div className={checklistItemStripeRailClassName(stripeItem, true)} aria-hidden />
                            <div className="min-w-0 flex-1">
                              <div className="px-3 py-2">
                                <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between sm:gap-3">
                                  <div className={`min-w-0 flex-1 font-medium leading-snug break-words text-pretty text-blue-100/90`} title={titleText}>
                                    {titleText}
                                  </div>
                                  <div className="flex flex-wrap items-center gap-1 sm:shrink-0 sm:justify-end">
                                    <AppButton type="button" variant="ghost" onClick={() => toggleWartungStep(it.id)} className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] leading-none border transition ${checklistErledigenGhostClasses(erledigt, true)}`} aria-pressed={erledigt} title={erledigt ? "Als offen markieren" : "Als erledigt markieren"}>
                                      <Check className="w-3 h-3" />
                                      {erledigt ? "Erledigt" : "Erledigen"}
                                    </AppButton>
                                  </div>
                                </div>
                                <div className="mt-1.5 text-[11px] flex items-center gap-1.5 flex-wrap text-blue-200/75">
                                  {erledigt ? <>
                                      <span className="inline-flex items-center gap-1">
                                        ✅ erledigt {fmtDateShort(it.erledigt_am)}
                                      </span>
                                      {it.erledigt_von_name ? <ZuweisungBadge user={{
                              id: it.erledigt_von || null,
                              name: it.erledigt_von_name,
                              avatar_url: it.erledigt_von_avatar_url || null
                            }} variant="full" size="xs" truncateAt={22} /> : null}
                                    </> : <span className="text-muted">Offen</span>}
                                </div>
                              </div>
                            </div>
                          </div>;
                      })}
                    </div>
                  </div>}
              </div>
            </div>

            <div className="h-px bg-divider" />

            <div className="rounded-2xl border border-default bg-surface overflow-hidden">
              <div className="px-3 sm:px-4 pt-3 pb-2 border-b border-default flex flex-wrap items-center gap-2">
                <div className="font-semibold text-primary flex items-center gap-2 min-w-0">
                  <span aria-hidden>📄</span>
                  <span>Abschlussprotokoll</span>
                </div>
                <div className="text-[11px] px-2 py-1 rounded-md border border-muted bg-white/[0.03] text-muted">
                  {protokolleByService.length === 1 ? "1 Protokoll" : protokolleByService.length ? `${protokolleByService.length} Protokolle` : "Kein Protokoll"}
                </div>
              </div>

              <div className="px-3 sm:px-4 pb-3 sm:pb-4 pt-3 space-y-3">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:gap-3 md:col-span-2">
                  <label htmlFor={fieldId("proto-result")} className="text-sm flex flex-col gap-1.5 flex-1 min-w-0">
                    Ergebnis
                    <Select id={fieldId("proto-result")} className={inputCls} value={protoResult} onChange={e => setProtoResult(e.target.value)}>
                      <option value="IO">Alles in Ordnung</option>
                      <option value="MASSNAHMEN_ERFORDERLICH">Massnahmen erforderlich</option>
                      <option value="EMPFOHLEN">Massnahmen empfohlen</option>
                    </Select>
                  </label>
                  <div className="w-fit max-w-full rounded-lg border border-white/[0.06] bg-white/[0.015] px-2 py-1.5 flex flex-wrap items-center gap-2 shrink-0">
                    <span className="text-xs text-muted whitespace-nowrap shrink-0">Vorschau:</span>
                    <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-semibold whitespace-nowrap ${protokollResultTone(protoResult)}`}>
                      {protokollResultLabel(protoResult)}
                    </span>
                  </div>
                </div>
                <label htmlFor={fieldId("proto-massnahmen")} className="text-sm md:col-span-2">
                  Massnahmen
                  <Textarea id={fieldId("proto-massnahmen")} className={inputCls} rows={4} placeholder="Optional: Hinweise oder empfohlene Nacharbeiten für den Kunden." value={protoMassnahmen} onChange={e => setProtoMassnahmen(e.target.value)} />
                </label>
              </div>

              <div className="rounded-xl border border-muted bg-white/[0.02] p-2 sm:p-2.5 flex flex-wrap items-center justify-between gap-2">
                <div className="text-xs text-blue-200/80 min-w-0 flex-1">
                  Speichert Ergebnis und aktuelle Checkliste für diesen Auftrag.
                </div>
                <AppButton type="button" variant="success" size="sm" disabled={!activeServiceId || creatingProto} className={creatingProto ? successButtonLoadingClassName : ""} onClick={createAbschlussprotokollForService}>
                  {creatingProto ? "Wird erstellt…" : "Protokoll erstellen"}
                </AppButton>
              </div>

              <div className="pt-1">
                <div className="text-xs uppercase text-muted mb-2">Vorhandene Protokolle</div>
                {protokolleByService.length ? <div className="space-y-2">
                    {protokolleByService.map(p => <div key={p.id} className="flex justify-between items-center border border-white/10 px-4 py-3 rounded-xl bg-white/5 hover:bg-white/[0.07] max-sm:flex-col max-sm:items-start max-sm:gap-2 transition-colors">
                        <div className="min-w-0 w-full sm:w-auto">
                          <div className="flex flex-wrap items-center gap-2">
                            <span className={`text-xs px-2 py-0.5 rounded-full border font-semibold ${protokollResultTone(p.status_result)}`}>
                              {protokollResultLabel(p.status_result)}
                            </span>
                            <span className={`text-xs ${"text-blue-200/70"}`}>{fmtDate(p.created_at)}</span>
                          </div>
                          {p.massnahmen_text ? <div className={`mt-1 text-xs truncate ${"text-blue-200/70"}`}>
                              Massnahmen: {p.massnahmen_text}
                            </div> : null}
                        </div>
                        <div className="flex shrink-0 items-center max-sm:w-full max-sm:flex-wrap max-sm:justify-start sm:justify-end gap-2">
                          <AppButton type="button" variant="secondary" size="sm" className={fileListPreviewActionClassName} disabled={exportingProtoId === p.id || deletingProtoId === p.id} onClick={() => exportAbschlussprotokoll(p.id)} aria-label="Abschlussprotokoll als PDF exportieren">
                            {exportingProtoId === p.id ? "Exportiere…" : "PDF Export"}
                          </AppButton>
                          <AppButton type="button" variant="secondary" size="sm" className={fileListDeleteActionClassName} disabled={deletingProtoId === p.id || exportingProtoId === p.id} onClick={() => deleteAbschlussprotokoll(p.id)} aria-label="Protokoll löschen" title="Protokoll löschen">
                            {deletingProtoId === p.id ? "…" : "Löschen"}
                          </AppButton>
                        </div>
                      </div>)}
                  </div> : <div className="text-sm text-muted">Noch kein Protokoll für diesen Auftrag.</div>}
              </div>
            </div>
            </div>
                      </div>
                    </> : null}
                </div>
              })}
            </div>}
        </div>
      </CollapsibleBlock>

      </div>
      <PdfExportActionSheet
        open={Boolean(abschlussPdfSheet)}
        onClose={() => setAbschlussPdfSheet(null)}
        pdfUrl={abschlussPdfSheet?.pdfUrl}
        title={abschlussPdfSheet?.title || "Abschlussprotokoll PDF"}
        fileName={abschlussPdfSheet?.fileName || "protokoll.pdf"}
        previewModule={abschlussPdfSheet?.previewModule}
        fileId={abschlussPdfSheet?.fileId}
      />
    </main>;
}